// A phone's keyboard put away by the back key (Android) leaves the focus
// in the text box: the caret stays, the screen keeps its typing layout and
// nothing is written until something else is tapped. The tensor app takes
// the focus away when the keyboard goes (a cell or a budget is written as
// when it is left by a tap), and its bottom tabs make room while the
// keyboard is up. Seen from the page: with the keyboard up the window is
// shorter (interactive-widget=resizes-content), and the tallest height
// seen at this width is the window without it.

/** How much shorter the window is with a keyboard (a toolbar coming and going is less). */
const OPEN_PX = 120
/** Within this of the tallest, the keyboard is gone. */
const CLOSED_PX = 60

/** Window sizes in, "the keyboard came" or "went" out ("turned": a new
    width, where nothing is known yet). */
export class KeyboardWatch {
  private width = -1
  private tallest = 0
  private up = false

  get open(): boolean {
    return this.up
  }

  feed(width: number, height: number): 'open' | 'closed' | 'turned' | null {
    if (width !== this.width) {
      // turned (or first seen): start over at this width
      const seen = this.width >= 0
      this.width = width
      this.tallest = height
      this.up = false
      return seen ? 'turned' : null
    }
    if (height > this.tallest) this.tallest = height
    if (!this.up && height < this.tallest - OPEN_PX) {
      this.up = true
      return 'open'
    }
    if (this.up && height > this.tallest - CLOSED_PX) {
      this.up = false
      return 'closed'
    }
    return null
  }
}

let holdUntil = 0

/** No blur for a while: a box gives up the focus and takes it back on
    purpose (endComposition), or its keyboard is changed (inputmode). */
export function holdKeyboard(ms = 800): void {
  holdUntil = Date.now() + ms
}

function isTextBox(el: Element | null): el is HTMLElement {
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLInputElement)
    return !['checkbox', 'radio', 'range', 'color', 'file', 'button', 'submit', 'reset', 'image'].includes(el.type)
  return el instanceof HTMLElement && el.isContentEditable
}

/**
 * Watches a phone's keyboard: while it is up, <html> has the class kb-open
 * (the screen's bottom tabs make room); when it goes and stays gone
 * (keyboards are swapped by going and coming back at once), the focused
 * text box is blurred. Returns the stop function.
 */
export function watchKeyboard(): () => void {
  const watch = new KeyboardWatch()
  const root = document.documentElement
  let timer = 0
  const check = () => {
    const ev = watch.feed(window.innerWidth, window.innerHeight)
    if (ev === null) return
    root.classList.toggle('kb-open', ev === 'open')
    clearTimeout(timer)
    // turned: the tabs come back, the typing goes on
    if (ev !== 'closed') return
    timer = window.setTimeout(() => {
      if (watch.open || Date.now() < holdUntil) return
      const el = document.activeElement
      if (isTextBox(el)) el.blur()
    }, 300)
  }
  check()
  window.addEventListener('resize', check)
  return () => {
    window.removeEventListener('resize', check)
    clearTimeout(timer)
    root.classList.remove('kb-open')
  }
}
